import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { observer } from 'mobx-react-lite';
import { MouseEvent, PropsWithChildren } from 'react';
import { createPortal } from 'react-dom';

import { transitionIn, transitionOut } from '@/constants/animations';

import styles from './Dialog.module.scss';

type DialogProps = {
  children?: React.ReactNode;
  onClose?: () => void;
  isOpen?: boolean;
  canClose?: boolean;
};

type StyleProps = {
  className?: string;
  style?: React.CSSProperties;
};

type ImageProps = {
  src: string;
  type?: 'lg' | 'sm';
  width?: number | string;
  height?: number | string;
} & DialogProps &
  StyleProps;

const Base = observer(
  ({
    style,
    className,
    children,
    onClose,
    isOpen,
    canClose,
  }: DialogProps & StyleProps) => {
    const handleOutsideClick = (e: MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();

      onClose && onClose();
    };

    return (
      <>
        {createPortal(
          <AnimatePresence>
            {isOpen && (
              <motion.div
                className={clsx(styles.dialog, className)}
                style={{ visibility: isOpen ? 'visible' : 'hidden' }}
              >
                <motion.div
                  className={styles.overlay}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, transition: transitionIn }}
                  onClick={handleOutsideClick}
                  transition={transitionOut}
                />
                <motion.div
                  className={styles.wrapper}
                  initial={{ y: 500 }}
                  animate={{ y: 0 }}
                  exit={{ y: 500, transition: transitionIn }}
                  transition={transitionOut}
                  style={style}
                >
                  {onClose && canClose && (
                    <button className={styles.close} onClick={onClose}>
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6 6L12 12M18 18L12 12M12 12L18 6M12 12L6 18"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  )}
                  {children}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        )}
      </>
    );
  },
);

const Title = observer<PropsWithChildren & StyleProps>(
  ({ children, className, style }) => (
    <h2 className={clsx('in-text-h1 in-text-center', className)} style={style}>
      {children}
    </h2>
  ),
);

const Main = observer<PropsWithChildren & StyleProps>(
  ({ children, className, style }) => (
    <div className={clsx(styles.main, className)} style={style}>
      {children}
    </div>
  ),
);

const Header = observer<PropsWithChildren & StyleProps>(
  ({ children, className, style }) => (
    <div className={clsx(styles.header, className)} style={style}>
      {children}
    </div>
  ),
);

const Footer = observer<PropsWithChildren & StyleProps>(
  ({ children, className, style }) => (
    <footer className={clsx(styles.footer, className)} style={style}>
      {children}
    </footer>
  ),
);

const Text = observer<PropsWithChildren & StyleProps>(
  ({ children, className, style }) => (
    <div
      className={clsx(
        'in-text-16 in-color-contrast in-text-center',
        styles.text,
        className,
      )}
      style={style}
    >
      {children}
    </div>
  ),
);

const Image = observer<ImageProps>(
  ({
    src,
    type = 'lg',
    style,
    className,
    children,
    width = 'auto',
    height = 'auto',
  }) => (
    <div
      className={clsx(
        styles.icon,
        { [styles[`icon--${type}`]]: type },
        className,
      )}
      style={style}
    >
      <img width={width} height={height} src={src} alt="" />
      {children}
    </div>
  ),
);

const ScrollContainer = observer<PropsWithChildren & StyleProps>(
  ({ className, style, children }) => (
    <div className={clsx(className)} style={{ ...style, overflow: 'auto' }}>
      {children}
    </div>
  ),
);

export const Dialog = {
  Base,
  Title,
  Main,
  Footer,
  Text,
  Header,
  Image,
  ScrollContainer,
};
